import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { applySeo } from "@/lib/seo";
import { Calculator, Leaf, Zap, Gauge, ClipboardCheck, FileCheck2, ArrowUpRight, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IMG = {
  rendite: `${BACKEND_URL}/api/images/hero-wind.jpg`,
  co2: `${BACKEND_URL}/api/images/home-solar.jpg`,
  premium: `${BACKEND_URL}/api/images/home-architecture.jpg`,
  hebel: `${BACKEND_URL}/api/images/home-editorial.jpg`,
  quiz: `${BACKEND_URL}/api/images/article-etfs-cover.jpg`,
  praeferenz: `${BACKEND_URL}/api/images/article-esg-cover.jpg`,
};

const TOOLS = [
  {
    to: "/rechner/nachhaltigkeits-rendite",
    icon: Calculator,
    tag: "Rendite",
    title: "Nachhaltigkeits-Rendite-Rechner",
    body:
      "Wie entwickelt sich ein ESG-ETF-Sparplan über 5, 15 oder 30 Jahre? Der Rechner zeigt Endkapital, Wertzuwachs und Renditepfad – transparent aufgeschlüsselt.",
    image: IMG.rendite,
    alt: "Windkraftanlagen im Abendlicht – Symbolbild Nachhaltigkeits-Rendite",
    cta: "Zum Rechner",
  },
  {
    to: "/rechner/co2-fussabdruck",
    icon: Leaf,
    tag: "Emissionen",
    title: "CO₂-Fußabdruck-Portfolio-Rechner",
    body:
      "Wie stark sinken die Emissionen Ihres Depots, wenn Sie von klassisch zu ESG oder Impact wechseln? Messbarer Vergleich statt Kaffeesatzleserei.",
    image: IMG.co2,
    alt: "Solar-Photovoltaikanlage – Symbolbild CO₂-Fußabdruck",
    cta: "Zum Rechner",
  },
  {
    to: "/rechner/green-premium",
    icon: Zap,
    tag: "Kosten",
    title: "Green Premium Rechner",
    body:
      "Was kostet der Wechsel von klassisch zu nachhaltig langfristig? Der Rechner beziffert die Opportunitätskosten Ihres guten Gewissens.",
    image: IMG.premium,
    alt: "Modernes nachhaltiges Bürogebäude – Symbolbild Green Premium",
    cta: "Zum Rechner",
  },
  {
    to: "/rechner/gruener-hebel",
    icon: Gauge,
    tag: "Wirkung",
    title: "Grüner Hebel-Simulator",
    body:
      "Übersetzt die CO₂-Wirkung Ihrer Geldanlage in Alltagsvergleiche: von veganer Ernährung über Autoverzicht bis zu gepflanzten Bäumen.",
    image: IMG.hebel,
    alt: "Redaktioneller Schreibtisch mit Recherche­material – Symbolbild Grüner Hebel",
    cta: "Zum Simulator",
  },
  {
    to: "/rechner/nachhaltiger-etf-finder",
    icon: ClipboardCheck,
    tag: "Passung",
    title: "ESG-Fit-Quiz",
    body:
      "In fünf kurzen Fragen zu einem nachhaltigen ETF, der zu Ihrem Anlageprofil passt: Ausschlüsse, Risiko, Kosten, Region, Ausschüttung.",
    image: IMG.quiz,
    alt: "Abstrakte Portfolio-Grafik – Symbolbild ESG-Fit-Quiz",
    cta: "Zum Quiz",
  },
  {
    to: "/rechner/nachhaltigkeitspraeferenz-assistent",
    icon: FileCheck2,
    tag: "Beratung",
    title: "Nachhaltigkeitspräferenz-Assistent",
    body:
      "Bereitet Sie in fünf Minuten auf das Beratungsgespräch mit Ihrer Bank vor – inklusive PDF zum Ausdrucken und Mitnehmen.",
    image: IMG.praeferenz,
    alt: "ESG-Bericht auf einem Schreibtisch – Symbolbild Nachhaltigkeitspräferenz-Assistent",
    cta: "Zum Assistenten",
    badge: "Neu",
  },
];

export default function RechnerHub() {
  useEffect(() => {
    applySeo({
      title: "Rechner & Tools – Übersicht | Grüne Geldanlage",
      description:
        "Sechs interaktive Werkzeuge für nachhaltige Geldanlage: Rendite-Rechner, CO₂-Fußabdruck, Green Premium, Grüner Hebel, ESG-Fit-Quiz und Nachhaltigkeitspräferenz-Assistent.",
      canonicalPath: "/rechner",
    });
    const setMeta = (sel, content) => {
      const m = document.querySelector(sel);
      if (m) m.setAttribute("content", content);
    };
    setMeta('meta[property="og:title"]', "Rechner & Tools – Grüne Geldanlage");
    setMeta('meta[property="og:description"]', "Sechs redaktionell entwickelte Werkzeuge für Rendite, Emissionen, Kosten, Wirkung, ETF-Passung und Nachhaltigkeitspräferenz.");
    setMeta('meta[name="twitter:title"]', "Rechner & Tools – Grüne Geldanlage");
    setMeta('meta[name="twitter:description"]', "Übersicht aller sechs interaktiven Werkzeuge für nachhaltige Geldanlage.");
  }, []);

  const jsonLdList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: TOOLS.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.title,
      url: `https://gruene-geldanlage.com${t.to}`,
    })),
  };
  const jsonLdBreadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: "https://gruene-geldanlage.com/" },
      { "@type": "ListItem", position: 2, name: "Rechner & Tools", item: "https://gruene-geldanlage.com/rechner" },
    ],
  };

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="rechner-hub-page">
      <Header />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdList) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumbs) }} />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-14" data-testid="rechner-hub-hero">
        <nav className="text-xs text-neutral-500 mb-6" aria-label="Brotkrumen">
          <Link to="/" className="hover:text-brand-primary">Start</Link>
          <ChevronRight size={12} className="inline mx-1" />
          <span className="text-neutral-700">Rechner &amp; Tools</span>
        </nav>
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-4">Werkzeuge · Übersicht</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-brand-dark mb-6">
              Unsere Rechner <em className="italic text-brand-primary">&amp; Tools</em>.
            </h1>
            <p className="text-lg text-neutral-700 max-w-2xl leading-relaxed">
              Sechs interaktive Werkzeuge, entwickelt für Privatanleger, die nachhaltiges Investieren
              nicht mit Bauchgefühl, sondern mit Zahlen entscheiden. Jedes Tool hat eine eigene
              Seite mit ausführlicher Erklärung. Keine Registrierung, keine Datenspeicherung – die
              Berechnung läuft in Ihrem Browser.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="inline-flex flex-col text-sm text-neutral-600 md:items-end">
              <div className="font-mono text-4xl text-brand-primary leading-none mb-1">6</div>
              <div>Werkzeuge</div>
              <div className="mt-3 font-mono text-4xl text-brand-primary leading-none mb-1">0</div>
              <div>Datenübertragungen mit Ihren Eingaben</div>
            </div>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="rechner-hub-grid">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <article
                key={t.to}
                className="group bg-white border border-neutral-200 hover:border-brand-primary transition-colors flex flex-col"
                data-testid={`hub-card-${t.to.split("/").pop()}`}
              >
                <Link to={t.to} className="block relative aspect-[16/10] overflow-hidden bg-neutral-100 no-underline">
                  <img
                    src={t.image}
                    alt={t.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-1.5 text-xs uppercase tracking-[0.15em] text-brand-dark">
                    <Icon size={14} strokeWidth={1.75} className="text-brand-primary" />
                    {t.tag}
                  </div>
                  {t.badge && (
                    <div className="absolute top-4 right-4 bg-brand-accent text-brand-dark px-2 py-1 text-[11px] uppercase tracking-widest font-semibold">
                      {t.badge}
                    </div>
                  )}
                </Link>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <h2 className="font-serif text-2xl font-medium text-brand-dark mb-3 leading-snug">
                    <Link to={t.to} className="no-underline hover:text-brand-primary">{t.title}</Link>
                  </h2>
                  <p className="text-neutral-600 leading-relaxed flex-1 mb-5">{t.body}</p>
                  <Link
                    to={t.to}
                    data-testid={`hub-cta-${t.to.split("/").pop()}`}
                    className="inline-flex items-center gap-2 self-start bg-brand-primary text-white px-4 py-2.5 text-sm font-medium tracking-wide no-underline hover:bg-brand-hover transition-colors"
                  >
                    {t.cta} <ArrowUpRight size={14} strokeWidth={1.75} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="bg-white border-y border-neutral-200" data-testid="rechner-hub-editorial">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
          <div className="overline mb-3">Warum diese Werkzeuge</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-6">
            Zahlen schlagen Bauchgefühl – auch bei nachhaltigen Geldanlagen.
          </h2>
          <div className="prose-editorial max-w-none">
            <p>
              Nachhaltiges Investieren ist kein Glaubenskampf, sondern eine Frage der
              Priorisierung. Möchten Sie den Klima-Impact Ihres Depots reduzieren? Wissen, was
              der Wechsel von klassisch zu ESG langfristig kostet? Oder verstehen, welche
              Ausschlüsse und Mindestanteile Ihr Berater in das Beratungsprotokoll eintragen soll?
              Für jede dieser Fragen gibt es hier ein eigenes Werkzeug – redaktionell entwickelt,
              nicht von einem Fondsanbieter finanziert, mit klarem Blick auf Chancen und
              Grenzen.
            </p>
            <h3>Prinzipien, denen wir folgen</h3>
            <p>
              Alle unsere Rechner rechnen mit realistischen Annahmen und weisen diese offen aus.
              Wir zeigen die typischen Bandbreiten, nicht die schmeichelhaftesten Extremwerte.
              Kostenkomponenten wie TER, Vorabpauschale und Steuern werden – wo relevant –
              berücksichtigt. Und alle Eingaben bleiben in Ihrem Browser: Kein Konto, kein
              Tracking, keine Weiterleitung an Fondsanbieter.
            </p>
            <h3>Wie Sie die Werkzeuge sinnvoll kombinieren</h3>
            <p>
              Wer für einen ETF-Sparplan plant, startet mit dem{" "}
              <Link to="/rechner/nachhaltigkeits-rendite">Nachhaltigkeits-Rendite-Rechner</Link>{" "}
              für die Größenordnung, prüft mit dem{" "}
              <Link to="/rechner/green-premium">Green Premium Rechner</Link> die
              Opportunitätskosten und findet mit dem{" "}
              <Link to="/rechner/nachhaltiger-etf-finder">ESG-Fit-Quiz</Link> ein passendes
              Produktprofil. Der{" "}
              <Link to="/rechner/co2-fussabdruck">CO₂-Fußabdruck-Portfolio-Rechner</Link> und der{" "}
              <Link to="/rechner/gruener-hebel">Grüne Hebel-Simulator</Link> machen die
              ökologische Wirkung greifbar. Vor dem Gang zur Bank runden Sie das Ganze mit dem{" "}
              <Link to="/rechner/nachhaltigkeitspraeferenz-assistent">
                Nachhaltigkeitspräferenz-Assistenten
              </Link>{" "}
              ab – inklusive PDF für das Beratungsgespräch.
            </p>
            <h3>Weiterführende Inhalte</h3>
            <p>
              Für die Einordnung dieser Werkzeuge in den größeren Marktkontext empfehlen wir
              unseren aktuellen{" "}
              <Link to="/nachhaltige-geldanlage-deutschland">Marktreport zur nachhaltigen
              Geldanlage in Deutschland</Link> sowie unsere Grundlagenartikel zu{" "}
              <Link to="/artikel/esg-kriterien-einfach-erklaert">ESG-Kriterien</Link> und zum
              Erkennen von{" "}
              <Link to="/artikel/greenwashing-erkennen">Greenwashing</Link>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
